import React, { memo, useMemo } from 'react';
import { defaultTheme } from '@styles/theme.styled';
import { RichTextBlock } from 'prismic-reactjs';
import { ImageProps } from '@components/image';
import Text from '@components/text';
import getLinkConfig from '@helpers/get-link-config';
import Section from '@components/section';
import AnimatedElement from '@components/animated-element';
import { Wrapper } from '@components/wrapper.styled';
import {
  TitleContainer,
  ImageContainer,
  StyledLinkComponent,
  TextContent,
  FeatureWrapper,
  StyledImage,
  StyledText,
  FeatureItemWrapper,
  FeatureListWrapper,
  FeatureListIcon,
  FeatureListDescription,
  IconWrapper,
  Divider,
} from './feature.styled';

export interface FeatureItemProps {
  icon?: ImageProps;
  description?: {
    richText: RichTextBlock[];
    text: string;
  };
}

export interface FeatureProps {
  image?: ImageProps;
  imageFit?: boolean;
  imageAlignment?: 'default' | 'left' | 'right';
  title?: string;
  subheading?: RichTextBlock[];
  description?: RichTextBlock[];
  backgroundColor?: keyof typeof defaultTheme.backgroundColors;
  link?: {
    uid?: string;
    url?: string;
  };
  linkLabel?: string;
  'aria-label'?: string;
  items: FeatureItemProps[];
}

const Feature: React.FC<FeatureProps> = ({
  image,
  imageFit,
  title,
  subheading,
  description,
  link,
  linkLabel,
  backgroundColor = 'white',
  imageAlignment = 'default',
  items,
  'aria-label': ariaLabel,
}) => {
  const linkConfig = getLinkConfig(link);
  const hasImage = Boolean(image?.gatsbyImageData || image?.url);
  const hasSubheading = Boolean(subheading?.length);
  const hasDescription = Boolean(description?.length);
  const hasLink = Boolean(linkConfig.url);
  const hasTitleOrSubheading = Boolean(title || hasSubheading);

  const FeatureList = useMemo(() => {
    return (
      <FeatureListWrapper itemCount={items.length}>
        {items.map(
          (item) =>
            item.icon?.url && (
              <FeatureItemWrapper key={`feature-list-${item.description?.richText[0].text || ''}`}>
                <IconWrapper>
                  <FeatureListIcon
                    url={item.icon.url}
                    alt={item.icon.alt}
                    gatsbyImageData={item.icon.gatsbyImageData}
                    loading="lazy"
                    objectFit="contain"
                  />
                </IconWrapper>
                <FeatureListDescription>
                  <Text
                    type="description"
                    richText={item.description?.richText}
                    margin="none"
                    fontSize="body"
                  />
                </FeatureListDescription>
              </FeatureItemWrapper>
            )
        )}
      </FeatureListWrapper>
    );
  }, [items]);

  return (
    <Section backgroundColor="white">
      <Wrapper>
        <FeatureWrapper
          bgColor={backgroundColor}
          role="article"
          $imageAlignment={imageAlignment}
          aria-label={ariaLabel || title}
        >
          {hasImage && (
            <ImageContainer hasItems={items.length > 0}>
              <StyledImage
                url={image?.url || ''}
                alt={image?.alt || title || 'Feature image'}
                gatsbyImageData={image?.gatsbyImageData}
                loading="lazy"
                objectPosition={items.length > 0 ? '0% 50%' : '50% 50%'}
                objectFit={imageFit ? 'cover' : 'contain'}
              />
            </ImageContainer>
          )}
          <TextContent hasItems={items.length > 0}>
            {hasTitleOrSubheading && (
              <TitleContainer>
                <AnimatedElement
                  animationType="scaleX"
                  delay={0.125}
                  threshold={0.125}
                  rootMargin="50px"
                >
                  <Divider style={{ transformOrigin: '50% 0' }} />
                </AnimatedElement>
                {title && (
                  <Text
                    tag="h2"
                    type="title"
                    stringText={title}
                    fontSize="heading4"
                    margin="none"
                    fontColor="black"
                  />
                )}
                {hasSubheading && (
                  <Text
                    type="subheading"
                    richText={subheading}
                    margin="xxs"
                    fontColor="darkPurple"
                    fontSize="large"
                  />
                )}
              </TitleContainer>
            )}
            {hasDescription && (
              <StyledText type="description" richText={description} margin="xxs" fontColor="dark" />
            )}
            {hasLink && (
              <StyledLinkComponent
                displayArrow={false}
                type="button"
                isExternal={linkConfig.isExternal}
                url={linkConfig.url}
                title={linkLabel || 'Explore all products and features'}
                aria-label={linkLabel || 'Explore all products and features'}
              />
            )}
            {items.length > 0 && FeatureList}
          </TextContent>
        </FeatureWrapper>
      </Wrapper>
    </Section>
  );
};

export default memo(Feature);
